// ============================================
// MOON MAP GENERATOR - Enhanced Implementation
// With accurate shading, libration, displacement & normal maps
// Includes bridge exports for moon-myjs.js integration
// ============================================

(function() {
    'use strict';

    // Plain-DOM helpers (this file no longer needs jQuery).
    function first(sel) { return document.querySelector(sel); }
    function all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
    function getVal(sel) { var el = first(sel); return el ? el.value : undefined; }
    function setVal(sel, v) { all(sel).forEach(function (el) { el.value = v; }); }
    function setText(sel, v) { all(sel).forEach(function (el) { el.textContent = v; }); }
    function setHTML(sel, v) { all(sel).forEach(function (el) { el.innerHTML = v; }); }

    // ============================================
    // STATE
    // ============================================
    let scene, camera, renderer, moonMesh, moonGroup;
    let currentDate = new Date();
    let currentLocation = { lat: 51.5074, lng: -0.1278, name: 'London, UK' };
    
    // ============================================
    // TEXTURE URLs - Your Skylit Studio Assets
    // Dual-quality: Preview (low-res for fast loading) and Render (high-res for export)
    // ============================================

    // Clarity/dehaze is BAKED into the default silver map (see the
    // SKYLIT_MOON_TEX script in moon-skylit.liquid, which supplies the theme
    // asset URL). Doing it offline rather than at runtime removes ~140ms of
    // blocked main thread per load and all three.js version sensitivity —
    // this theme runs r128, the print renderer runs r160, and the two name
    // the colour-space APIs differently.
    //
    // Falls back to the original texture if the global is absent, so the
    // customiser still works if the asset or the section script is missing.
    var _TEX = (typeof window !== 'undefined' && window.SKYLIT_MOON_TEX) || {};
    var SILVER = _TEX.silver ||
        'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_silver_v3-1k.jpg?v=1777886361';

    // Moon surface textures - silver, gold, and blue variants
    const MOON_TEXTURES = {
        silver: {
            preview: SILVER,
            render: SILVER
        },
        bright_silver: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_bright_silver_v3-1k.jpg?v=1777886361',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_bright_silver_v3-1k.jpg?v=1777886361'
        },
        gold: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-new-cont_cur-GOLDEN3.png?v=1764999529',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-new-cont_cur-GOLDEN3.png?v=1764999529'
        },
        blue: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-BLUE.jpg',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-BLUE.jpg'
        },
        pink: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-PINK.jpg',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-PINK.jpg'
        }
    };

    // All textures with preview and render quality versions
    const TEXTURES = {
        // Main moon texture
        moon: {
            preview: MOON_TEXTURES.silver.preview,
            render: MOON_TEXTURES.silver.render
        },

        // Displacement map - adds 3D crater depth
        displacement: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_displacement_v3-1k.jpg?v=1777886360',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_displacement_v3-1k.jpg?v=1777886360'
        },

        // Normal map - adds fine surface detail
        normal: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_normal_v3-1k.jpg?v=1777886360',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_normal_v3-1k.jpg?v=1777886360'
        }
    };

    // Track current texture quality mode
    var currentTextureQuality = 'preview';  // 'preview' or 'render'

    // Lighting - simplified for realistic look
    let lights = {
        ambient: null,      // Earthshine - subtle visibility on dark side
        directional: null   // Sun light - main illumination
    };

    // ============================================
    // BRIDGE EXPORTS - For moon-myjs.js integration
    // ============================================

    // Render-complete signal — consumed by the headless boot path
    // (moon-myjs.js → SkylitMoon.bootHeadless) so the CF Browser-Rendering
    // cart-thumbnail host doesn't screenshot a blank/black moon before the
    // first WebGL frame is drawn. render() (the single renderer.render
    // chokepoint) flips __moonRendered=true and resolves moonRenderComplete
    // after the first successful draw. Fail-open: bootHeadless caps the wait.
    var _moonRenderResolve;
    // (Re)create the render gate: fresh promise + resolver, flag cleared.
    // Called once at module init, and again by bootHeadless BEFORE hydration
    // so the gate waits for the HYDRATED re-render (the new date/location
    // phase), not the default-moon first render that already fired on boot.
    function armMoonRenderComplete() {
        window.__moonRendered = false;
        window.moonRenderComplete = new Promise(function(resolve) {
            _moonRenderResolve = resolve;
        });
    }
    armMoonRenderComplete();
    window.armMoonRenderComplete = armMoonRenderComplete;
    // Resample the halo tint after any draw. render() is the single
    // renderer.render chokepoint, so this covers texture-variant switches,
    // phase changes and restores alike — scheduling it only from
    // applyMoonTransforms missed variant switches entirely (gold/blue/pink
    // kept the silver tint).
    var _tintTimer = null;
    function scheduleGlowTint() {
        if (_tintTimer) clearTimeout(_tintTimer);
        _tintTimer = setTimeout(updateGlowTint, 90);
    }

    function signalMoonRendered() {
        if (!window.__moonRendered) {
            window.__moonRendered = true;
            if (_moonRenderResolve) _moonRenderResolve();
        }
    }

    // Expose current location for cart submission
    window.moonCurrentLocation = currentLocation;
    
    // Expose phase info (updated by updateMoon)
    window.moonPhaseInfo = {
        phase: 0,
        illumination: 0,
        phaseName: ''
    };
    
    // Function to update phase info (called in updateMoon)
    function updatePhaseInfo(moonIllum, phaseName) {
        window.moonPhaseInfo = {
            phase: moonIllum.phase,
            illumination: Math.round(moonIllum.fraction * 100),
            phaseName: phaseName
        };
    }

    // ============================================
    // MOON HALO
    // ============================================
    // Drives #moon-glow (see moon-skylit.css) via two CSS variables. The
    // halo itself is a single baked image; these are the only dynamic parts.
    //
    // updateGlowDirection points the halo at the Sun. Rather than re-deriving
    // the bright-limb
    // angle from χ / P / parallactic (three conventions to get wrong), read
    // the direction straight off the light after the scene graph has been
    // updated: the directional light is a child of moonGroup, so its world
    // position already carries every rotation applied to the moon.
    // The camera looks down the z axis at the origin with a 0.521° fov, so
    // world X/Y map 1:1 to screen X/Y. CSS angles run clockwise from "up",
    // which is atan2(x, y).

    // Halo hue follows the selected moon texture. Swapping a pre-tinted
    // asset rather than colouring at runtime: a background-color fills the
    // whole element box regardless of the art's alpha, and moving the colour
    // to a masked child broke compositing on Safari 18 (rendered as a dark
    // disc, with every computed style still correct).
    // Size the halo from the MEASURED canvas, never from the container.
    // #moon-canvas-container is 84% of the frame with aspect-ratio 1/1, and on
    // phone portrait the canvas fills it exactly — but in iPad landscape the
    // canvas came out 291px inside a 343px container, so a percentage-of-
    // container halo was 16% oversized and the ring floated off the limb
    // instead of hugging it. The art seats its limb at 0.2505 of its own box
    // and the disc is 0.495 of the canvas, hence the ratio below.
    var HALO_W_PER_CANVAS = 0.495 / 0.2505;
    // The on-screen width, unless the preview is transformed: the storefront's
    // preview stage (moon-stage.js) scales the print as the phone preview
    // collapses and lays it into a photo in Room view, and the halo is sized
    // in the canvas's own (untransformed) pixels.
    function layoutWidth(el) {
        var w = el.getBoundingClientRect().width;
        if (el.offsetWidth && Math.abs(w - el.offsetWidth) > 1.5) w = el.offsetWidth;
        return w;
    }
    function updateGlowSize() {
        var container = document.getElementById('moon-canvas-container');
        var canvas = document.getElementById('moon-canvas');
        if (!container || !canvas) return;
        var w = layoutWidth(canvas);
        if (w > 0) container.style.setProperty('--moon-halo-w',
            (w * HALO_W_PER_CANVAS).toFixed(1) + 'px');
        watchGlowSize(canvas);
    }

    // Watch the CANVAS, not just the container. updateGlowSize otherwise runs
    // only from render() and handleResize(), and handleResize is gated on the
    // CONTAINER's width changing — but in iPad landscape the canvas is
    // height-constrained and shrinks (291px) inside an unchanged 343px
    // container, so nothing would recompute and the halo would keep a stale
    // size. Observing the canvas covers every way its box can change.
    //
    // Note the disc fraction here is a CSS guarantee (clip-path:
    // circle(49.5%)), not a measurement — unlike the family moon, where
    // sampling it mid-resize produced badly wrong halo sizes.
    var _glowRO = null;
    function watchGlowSize(canvas) {
        if (_glowRO || typeof ResizeObserver === 'undefined') return;
        _glowRO = new ResizeObserver(function () {
            var container = document.getElementById('moon-canvas-container');
            var c = document.getElementById('moon-canvas');
            if (!container || !c) return;
            var w = layoutWidth(c);
            if (w > 0) container.style.setProperty('--moon-halo-w',
                (w * HALO_W_PER_CANVAS).toFixed(1) + 'px');
        });
        _glowRO.observe(canvas);
    }

    function updateGlowTint() {
        var glow = document.getElementById('moon-glow');
        var map = (typeof window !== 'undefined' && window.SKYLIT_MOON_HALOS) || null;
        if (!glow || !map) return;
        var url = map[window.currentMoonTexture] || map.silver;
        if (!url) return;
        var next = 'url("' + url + '")';
        if (glow.style.backgroundImage !== next) glow.style.backgroundImage = next;
    }

    function updateGlowDirection() {
        var container = document.getElementById('moon-canvas-container');
        if (!container || !lights.directional || !moonGroup) return;
        try {
            moonGroup.updateMatrixWorld(true);
            var p = new THREE.Vector3();
            lights.directional.getWorldPosition(p);
            if (!p.x && !p.y) return;   // light dead-on; keep the last angle
            var deg = Math.atan2(p.x, p.y) * 180 / Math.PI;
            container.style.setProperty('--moon-glow-angle', deg.toFixed(1) + 'deg');
        } catch (e) {
            /* keep whatever angle is already set */
        }
    }

    // Strength tracks the illuminated fraction so a thin crescent doesn't sit
    // inside a full-moon-sized bloom. Curved with pow(f, 0.6) because a
    // linear ramp makes everything between quarter and gibbous look alike.
    function updateGlowStrength(fraction) {
        var container = document.getElementById('moon-canvas-container');
        if (!container) return;
        var f = Math.max(0, Math.min(1, fraction || 0));
        container.style.setProperty('--moon-glow-strength',
            (0.30 + 0.70 * Math.pow(f, 0.6)).toFixed(3));
    }

    // The halo is additive (screen blend), which is invisible on white and
    // cream paper — there it flips to a cast shadow instead. Rather than
    // hooking each of the several places moon-myjs.js can change the
    // background (swatch click, saved-draft restore, magical background
    // image, clearing one), observe the frame's style attribute so every
    // path is covered by construction.
    function initGlowBackgroundWatch() {
        var frame = document.querySelector('.custom-frame');
        if (!frame) return;

        // NEVER write the attribute unless the value actually changes.
        // classList.remove() of an ABSENT class still re-serialises and sets
        // the class attribute whenever the element already has classes (the
        // spec only skips the write when there is no class attribute at all),
        // and setting an attribute queues a mutation record even when the
        // value is identical. With 'class' observed, that made this function
        // retrigger itself forever and lock the main thread — the customiser
        // never finished loading and took the whole browser tab down with it.
        function setLight(want) {
            if (frame.classList.contains('moon-bg-light') === want) return;
            frame.classList.toggle('moon-bg-light', want);
        }

        // .custom-frame carries `transition: all 0.3s ease`, so
        // getComputedStyle().backgroundColor returns the value MID-ANIMATION.
        // Reading it the moment the style attribute changes gives the colour
        // being transitioned AWAY from — which left the halo one step behind
        // (pick white, halo stays additive; pick black, halo turns to a
        // shadow). Prefer the inline value: that is the target the customiser
        // just wrote, and it is not animated.
        function readBg() {
            var cs = window.getComputedStyle(frame);
            return {
                image: frame.style.backgroundImage || cs.backgroundImage,
                color: frame.style.backgroundColor || cs.backgroundColor
            };
        }

        function sync() {
            var bg = readBg();
            // A magical background image is always a dark galaxy — keep the
            // additive halo regardless of the underlying colour.
            if (bg.image && bg.image !== 'none') { setLight(false); return; }
            var m = (bg.color || '').match(/[\d.]+/g);
            if (!m || m.length < 3) return;
            // Transparent frame = the page behind it, which is dark.
            if (m.length > 3 && parseFloat(m[3]) === 0) { setLight(false); return; }
            var hsp = Math.sqrt(0.299 * m[0] * m[0] +
                                0.587 * m[1] * m[1] +
                                0.114 * m[2] * m[2]);
            setLight(hsp > 127.5);
        }

        // Belt and braces: re-check once the transition has actually settled,
        // in case a path ever sets the colour by class or stylesheet rather
        // than inline (where the inline target would be empty).
        var _settle = null;
        function syncSoon() {
            sync();
            if (_settle) clearTimeout(_settle);
            _settle = setTimeout(sync, 380);   // > the 300ms transition
        }

        sync();
        if (typeof MutationObserver === 'undefined') return;
        // Observe 'style' ONLY. The background is always changed via inline
        // style, and this callback's own write targets 'class' — observing
        // 'class' here is what closed the feedback loop.
        new MutationObserver(syncSoon).observe(frame, {
            attributes: true,
            attributeFilter: ['style']
        });
    }

    // Allow UI to switch moon texture variants
    function loadMoonTextureVariant(textureUrl, variantName) {
        if (!renderer || !moonMesh) {
            // Texture will be picked up when the moon is created
            return Promise.resolve();
        }

        showLoader();

        var loader = new THREE.TextureLoader();
        return loadTexture(loader, textureUrl, variantName || 'Moon Variant')
            .then(function(texture) {
                if (!texture) {
                    throw new Error('Texture failed to load');
                }

                moonMesh.material.map = texture;
                moonMesh.visible = true;
                moonMesh.material.needsUpdate = true;
                render();
            })
            .catch(function(error) {
                console.error('⚠️ Unable to apply moon texture variant:', error);
            })
            .finally(function() {
                hideLoader();
            });
    }

    // Track current moon texture variant globally
    window.currentMoonTexture = 'silver';

    window.setMoonTextureVariant = function(variant) {
        var textureConfig = MOON_TEXTURES[variant] || MOON_TEXTURES.silver;
        var textureUrl = textureConfig[currentTextureQuality] || textureConfig.preview;

        // Update default so subsequent loads use the selected texture
        TEXTURES.moon = textureConfig;

        // Track the current variant globally for submission
        window.currentMoonTexture = variant || 'silver';

        return loadMoonTextureVariant(textureUrl, 'Moon Variant: ' + variant);
    };

    // Function to load high-res textures for export
    function loadHighResTextures() {
        return new Promise(function(resolve) {
            if (!moonMesh || !moonMesh.material) {
                resolve();
                return;
            }

            var loader = new THREE.TextureLoader();
            var texturesLoaded = 0;
            var totalTextures = 3;

            function checkComplete() {
                texturesLoaded++;
                if (texturesLoaded >= totalTextures) {
                    render();
                    resolve();
                }
            }

            // Get the current texture variant's high-res URLs
            var moonTextureConfig = TEXTURES.moon;
            var moonUrl = moonTextureConfig.render || moonTextureConfig.preview;

            // Load high-res moon texture
            loader.load(moonUrl, function(texture) {
                texture.colorSpace = THREE.SRGBColorSpace;
                moonMesh.material.map = texture;
                moonMesh.visible = true;
                moonMesh.material.needsUpdate = true;
                checkComplete();
            }, undefined, checkComplete);

            // Load high-res displacement
            loader.load(TEXTURES.displacement.render, function(texture) {
                moonMesh.material.displacementMap = texture;
                moonMesh.material.needsUpdate = true;
                checkComplete();
            }, undefined, checkComplete);

            // Load high-res normal
            loader.load(TEXTURES.normal.render, function(texture) {
                moonMesh.material.normalMap = texture;
                moonMesh.material.needsUpdate = true;
                checkComplete();
            }, undefined, checkComplete);
        });
    }

    // Function to restore preview textures after export
    function loadPreviewTextures() {
        if (!moonMesh || !moonMesh.material) return;

        var loader = new THREE.TextureLoader();
        var moonTextureConfig = TEXTURES.moon;
        var moonUrl = moonTextureConfig.preview || moonTextureConfig.render;

        loader.load(moonUrl, function(texture) {
            texture.colorSpace = THREE.SRGBColorSpace;
            moonMesh.material.map = texture;
                moonMesh.visible = true;
            moonMesh.material.needsUpdate = true;
        });

        loader.load(TEXTURES.displacement.preview, function(texture) {
            moonMesh.material.displacementMap = texture;
            moonMesh.material.needsUpdate = true;
        });

        loader.load(TEXTURES.normal.preview, function(texture) {
            moonMesh.material.normalMap = texture;
            moonMesh.material.needsUpdate = true;
        });

        render();
    }

    // ============================================
    // GOOGLE MAPS CALLBACK - Entry Point
    // ============================================
    // Split into two halves so the section can boot the Three.js scene on
    // DOM-ready without waiting for Google Maps (which moon-skylit.liquid now
    // lazy-loads on first interaction):
    //   moonBindPlaces() — Places autocomplete only; safe to call any time,
    //                      no-ops until the Maps API is actually present.
    //   moonBootScene()  — date/time selectors + Three.js + textures + UI;
    //                      guarded against double-boot.
    // initMoonMap() keeps its original do-everything behaviour for backwards
    // compatibility (it remains the Maps callback on any template still
    // pointing at it).
    // Migrated from the legacy google.maps.places.Autocomplete widget (which
    // Google deprecated in 2025) to the new PlaceAutocompleteElement web
    // component — same approach as starmap-v2's bindPlacesAutocomplete().
    //
    // The new element renders its OWN input rather than decorating ours, so we
    // keep #address_form1 as a HIDDEN canonical mirror: on selection we write
    // the resolved text into it and fire `change`, which keeps every existing
    // listener (custom_add/custom_address sync, button text, cookie save,
    // autosave, reloadMoonWithAnimation) working unchanged. The visible
    // PlaceAutocompleteElement is mounted into #moon-location-wrapper.
    var _moonPlacesBound = false;
    window.moonBindPlaces = function() {
        if (_moonPlacesBound) return;
        if (!(window.google && google.maps && google.maps.places)) return;
        if (!google.maps.places.PlaceAutocompleteElement) return;
        var wrapper = document.getElementById("moon-location-wrapper");
        if (!wrapper) return;
        _moonPlacesBound = true;

        // UK-biased ranking (same config as starmap-v2). Neither field RESTRICTS
        // — international customers can still pick non-UK results, they just rank
        // lower in the dropdown.
        // SkylitPlaces swaps in our look-alike (assets/skylit-place-autocomplete.js),
        // which debounces typing and bills picks at the Essentials Places tier.
        var pacOptions = {
            requestedRegion: 'gb',
            locationBias: { north: 60.9, south: 49.5, east: 1.8, west: -8.7 }
        };
        var pac = window.SkylitPlaces
          ? window.SkylitPlaces.createAutocomplete(pacOptions)
          : new google.maps.places.PlaceAutocompleteElement(pacOptions);
        pac.id = 'moon-location-input';
        pac.className = 'moon-pac-element';
        // Seed the placeholder from the hidden mirror so a returning customer's
        // saved location (written into #address_form1 during hydration, which
        // runs before the user interacts and triggers the lazy Maps load) is
        // visible. Falls back to the London default hint.
        var seeded = '';
        try { seeded = (document.getElementById('address_form1') || {}).value || ''; } catch (e) { /* noop */ }
        try { pac.placeholder = seeded || 'London, UK'; } catch (e) { /* noop */ }
        try { pac.setAttribute && pac.setAttribute('placeholder', seeded || 'London, UK'); } catch (e) { /* noop */ }
        wrapper.insertBefore(pac, wrapper.firstChild);

        pac.addEventListener('gmp-select', async function(event) {
            try {
                var place = event.placePrediction.toPlace();
                await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] });
                var lat = place.location.lat();
                var lng = place.location.lng();
                // Label = tapped name + its country (e.g. "Manchester, UK"),
                // matching starmap-v2 — displayName drops the country and
                // formattedAddress is a postcoded street for POIs, so append the
                // country pulled from addressComponents.
                var name = place.displayName || '';
                var country = '';
                var comps = place.addressComponents || [];
                for (var ci = 0; ci < comps.length; ci++) {
                    var ctypes = comps[ci].types || [];
                    if (ctypes.indexOf('country') !== -1) {
                        var sc = comps[ci].shortText;
                        if (sc === 'GB') country = 'UK';
                        else if (sc === 'US') country = 'USA';
                        else country = comps[ci].longText || sc || '';
                        break;
                    }
                }
                var picked = name
                    ? ((country && name.indexOf(country) === -1) ? (name + ', ' + country) : name)
                    : (place.formattedAddress || '');

                // Route through setMoonLocation (moon-myjs.js) rather than
                // assigning currentLocation directly: that bridge is the ONLY
                // writer of the moon_lat_lng cookie. Assigning here left the
                // coords cookie unwritten while the change event below still
                // wrote moon_address, so a returning customer got their place
                // NAME restored on top of the 51.5074/-0.1278 London default
                // and the print's coordinate line said London under "Leeds".
                // (Order #190107, 2026-07-28. Regression introduced when the
                // customizer moved to PlaceAutocompleteElement.)
                if (typeof window.setMoonLocation === 'function') {
                    window.setMoonLocation(lat, lng, picked);
                } else {
                    // moon-myjs.js absent — keep the old inline behaviour so a
                    // load failure degrades to "no persistence", not "no map".
                    currentLocation.lat = lat;
                    currentLocation.lng = lng;
                    currentLocation.name = picked;
                    window.moonCurrentLocation = currentLocation;
                }

                // Mark that the user actively selected a location (even if it's
                // "London, UK") BEFORE firing change, so the section's change
                // handler doesn't flip the gate flag back off for the default.
                window.userHasEnteredLocation = true;

                // Mirror into the canonical hidden field + fire change so all
                // existing listeners run unchanged.
                var mirror = document.getElementById('address_form1');
                if (mirror) {
                    mirror.value = picked;
                    mirror.dispatchEvent(new Event('change', { bubbles: true }));
                }
                setVal('#custom_address', picked);
                setText('#custom_add', picked);
                // Selecting a new place re-prefills both printed-text overrides,
                // so any prior "deliberately cleared" state no longer applies.
                window.moonAddressOverrideCleared = false;
                window.moonCoordsOverrideCleared = false;

                console.log('📍 Location changed:', picked);
                // setMoonLocation -> setMoonLocationInternal already redraws the
                // coordinate line and reloads the moon. Only drive them here on
                // the fallback path, otherwise the moon animates twice.
                if (typeof window.setMoonLocation !== 'function') {
                    updateCoordinatesDisplay();
                    reloadMoonWithAnimation();
                }
            } catch (e) { console.warn('[moon] place select failed', e); }
        });

        // NOTE: no body-fixed "scroll lock" here (deliberately, unlike the
        // earlier star-map port). The moon customizer scrolls on the WINDOW
        // (the star's settings panel is an inner overflow-y:auto container), so
        // the lock's "scroll the field to the top" yanked the whole page up by
        // ~460px on focus and slammed it back on unlock — verified on-device
        // (iOS 26.6) as a jarring jump that also left the field stranded above
        // the shifted visual viewport. Letting iOS handle the focus natively
        // scrolls the field cleanly into view above the keyboard (measured:
        // field at top:199 inside the 0–447 visual viewport, full height, no
        // jump). The interactive-widget=resizes-content viewport (theme.liquid)
        // stays as the keyboard-aware hint.
    };

    var _moonSceneBooted = false;
    window.moonBootScene = function() {
        if (_moonSceneBooted) return;
        _moonSceneBooted = true;
        // Initialize components in order. Each step is isolated so a failure
        // in one (e.g. a UI helper running before a third-party dependency is
        // ready) can't take down the WebGL scene setup that follows it - that
        // would silently kill the moon preview with no error surfaced to the
        // customer.
        try { initDateTimeSelectors(); } catch (e) { console.warn('[moon] initDateTimeSelectors failed', e); }
        try { initThreeJS(); } catch (e) { console.warn('[moon] initThreeJS failed', e); }
        try { loadMoonTextures(); } catch (e) { console.warn('[moon] loadMoonTextures failed', e); }
        try { setupUI(); } catch (e) { console.warn('[moon] setupUI failed', e); }
    };

    window.initMoonMap = function() {
        console.log('🌙 Initializing Enhanced Moon Map...');
        window.moonBindPlaces();
        window.moonBootScene();
    };

    // ============================================
    // THREE.JS INITIALIZATION - Enhanced Lighting
    // ============================================
    function initThreeJS() {
        var canvas = document.getElementById('moon-canvas');
        var container = document.getElementById('moon-canvas-container');
        if (!canvas || !container) {
            console.error('❌ Canvas or container not found');
            return;
        }

        // Canvas now fills the container (CSS width:100%) — match the
        // renderer backing 1:1 so the moon stays crisp without browser upscale.
        var containerWidth = container.offsetWidth || 400;
        var size = Math.min(containerWidth, 600);

        // Transparent scene background — the page's CSS background shows
        // through the canvas everywhere outside the moon disk. Avoids the
        // black rim that appeared when the moon doesn't fill the canvas
        // edge-to-edge (e.g. with cameraZ = 225).
        scene = new THREE.Scene();
        scene.background = null;

        camera = new THREE.PerspectiveCamera(0.521, 1, 0.1, 1000);
        camera.position.z = 225;

        // Moon group for combined rotations (libration + phase orientation)
        moonGroup = new THREE.Object3D();
        scene.add(moonGroup);

        // Renderer with high quality PBR settings
        renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true,
            preserveDrawingBuffer: true,  // Required for canvas export
            alpha: true
        });
        renderer.setSize(size, size);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        // PBR rendering settings
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 0.80;

        // WebGL recovery. Without this the moon silently renders nothing when a
        // context is lost, which on mobile happens under ordinary memory
        // pressure. See snippets/webgl-recovery.liquid.
        if (window.SkylitWebGL && renderer && renderer.domElement) {
            window.SkylitWebGL.attach(renderer.domElement, {
                label: 'moon preview',
                critical: true,
                onRestore: function () {
                    try {
                        renderer.resetState && renderer.resetState();
                        updateMoon();
                        render();
                        return true;
                    } catch (e) {
                        return false;
                    }
                },
                onFail: function () {
                    var host = document.querySelector('#moon-canvas') || renderer.domElement;
                    window.SkylitWebGL.showFallback(host.parentNode || document.body, { label: 'moon preview' });
                }
            });
        }

        // ============================================
        // LIGHTING SETUP - PBR optimized illumination
        // ============================================

        // Ambient light - simulates earthshine (subtle light on dark side)
        // PBR needs slightly higher ambient for visible dark side detail
        lights.ambient = new THREE.AmbientLight(0x404050, 0.25);
        moonGroup.add(lights.ambient);

        // Directional light - simulates distant sun
        // PBR materials need higher intensity for proper illumination
        lights.directional = new THREE.DirectionalLight(0xffffff, 3.0);
        lights.directional.position.set(100, 0, 100);
        moonGroup.add(lights.directional);

        // NEW: subtle earthshine from the opposite side
        lights.earthshine = new THREE.DirectionalLight(0x777788, 1.08);
        lights.earthshine.position.set(-100, 0, -100); // roughly opposite the Sun
        moonGroup.add(lights.earthshine);

        // Handle resize — only when the moon CONTAINER's width has
        // actually changed. Mobile browsers fire `resize` for many
        // non-layout events: URL bar show/hide on scroll, pinch-to-zoom
        // (which can shift visualViewport without altering layout), soft
        // keyboard appearing, etc. The container's offsetWidth is the
        // only thing that matters to us, so we gate on that. If the
        // container is the same width as last check, do nothing.
        var lastContainerWidth = null;
        var moonContainer = document.getElementById('moon-canvas-container');
        if (moonContainer) lastContainerWidth = moonContainer.offsetWidth;
        window.addEventListener('resize', debounce(function () {
            var c = document.getElementById('moon-canvas-container');
            if (!c) return;
            var cw = c.offsetWidth;
            if (cw === lastContainerWidth) return;
            lastContainerWidth = cw;
            handleResize();
        }, 250));

        // Wave 26.3 — also observe the canvas container directly so renderer
        // resizes when the FRAME changes shape (aspect-ratio swap, container
        // query reflow, etc.) without needing a window resize. Required for
        // the upcoming starmap-style aspect-ratio frame migration: when
        // .custom-frame's aspect changes, #moon-canvas-container width
        // changes too, but window doesn't fire 'resize'.
        if (moonContainer && typeof ResizeObserver !== 'undefined') {
            var resizeRaf = null;
            var ro = new ResizeObserver(function(entries) {
                if (resizeRaf) cancelAnimationFrame(resizeRaf);
                resizeRaf = requestAnimationFrame(function() {
                    var cw = moonContainer.offsetWidth;
                    if (cw !== lastContainerWidth) {
                        lastContainerWidth = cw;
                        handleResize();
                    }
                });
            });
            ro.observe(moonContainer);
        }

        try { initGlowBackgroundWatch(); } catch (e) { console.warn('[moon] glow watch failed', e); }

        console.log('🎬 Three.js initialized with realistic lighting');
    }

    // ============================================
    // TEXTURE LOADING - Moon + Displacement + Normal
    // Uses preview quality for fast page load
    // ============================================
    function loadMoonTextures() {
        showLoader();

        var loader = new THREE.TextureLoader();

        // Get preview URLs (or render as fallback)
        var moonUrl = TEXTURES.moon.preview || TEXTURES.moon.render || TEXTURES.moon;
        var displacementUrl = TEXTURES.displacement.preview || TEXTURES.displacement.render || TEXTURES.displacement;
        var normalUrl = TEXTURES.normal.preview || TEXTURES.normal.render || TEXTURES.normal;

        console.log('🌙 Loading preview quality textures...');

        // Load all three textures in parallel
        Promise.all([
            loadTexture(loader, moonUrl, 'Moon (preview)'),
            loadTexture(loader, displacementUrl, 'Displacement (preview)'),
            loadTexture(loader, normalUrl, 'Normal (preview)')
        ])
        .then(function(textures) {
            var moonTexture = textures[0];
            var displacementTexture = textures[1];
            var normalTexture = textures[2];

            // Clarity must never be able to stall the boot. If anything in
            // the enhance path throws, fall straight back to the raw
            // texture — previously a throw here fell into .catch(), which
            // called createMoon again, threw again, and left the promise
            // rejected with hideLoader() never reached: a permanent
            // LOADING spinner.
            // Guarded: if createMoon throws, the old code fell into
            // .catch(), called createMoon again, threw again, and left the
            // promise rejected with hideLoader() never reached — a spinner
            // that never goes away. hideLoader() now runs regardless.
            try {
                createMoon(moonTexture, displacementTexture, normalTexture);
            } finally {
                hideLoader();
            }
            // First real render is up — fade out the static LCP poster the
            // section pre-paints (moon-skylit.liquid #moon-lcp-poster).
            var lcpPoster = document.getElementById('moon-lcp-poster');
            if (lcpPoster) { lcpPoster.style.opacity = '0'; setTimeout(function(){ lcpPoster.remove(); }, 600); }
            console.log('🌙 All preview textures loaded successfully!');
        })
        .catch(function(error) {
            console.error('⚠️ Texture loading error:', error);
            // Fallback: try loading just the moon texture. Raw — no clarity
            // pass on the recovery path, so this can't fail the same way
            // twice and leave the loader up forever.
            loader.load(moonUrl, function(texture) {
                createMoon(texture, null, null);
                hideLoader();
                var lcpPoster = document.getElementById('moon-lcp-poster');
                if (lcpPoster) { lcpPoster.style.opacity = '0'; setTimeout(function(){ lcpPoster.remove(); }, 600); }
                console.log('🌙 Loaded with fallback (moon texture only)');
            }, undefined, function (err) {
                // Last resort: the fallback texture failed too. Drop the
                // loader anyway — a bare frame beats a spinner that never
                // goes away, which is what the customer saw.
                console.error('❌ Moon texture unavailable:', err);
                hideLoader();
            });
        });
    }
    
    function loadTexture(loader, url, name) {
        return new Promise(function(resolve, reject) {
            loader.load(
                url,
                function(texture) {
                    // Optimize texture settings
                    texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
                    texture.minFilter = THREE.LinearMipmapLinearFilter;
                    texture.magFilter = THREE.LinearFilter;
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    console.log('✓ Loaded:', name);
                    resolve(texture);
                },
                function(progress) {
                    // Progress callback (optional)
                },
                function(error) {
                    console.warn('⚠️ Failed to load:', name, error);
                    resolve(null); // Don't reject, just return null
                }
            );
        });
    }

    // ============================================
    // MOON CREATION - MeshStandardMaterial for PBR Rendering
    // ============================================
    function createMoon(moonTexture, displacementTexture, normalTexture) {
        var geometry = new THREE.SphereGeometry(1, 512, 512);

        // PBR Material configuration - physically accurate moon surface
        var materialConfig = {
            color: 0xf5f5f5,
            map: moonTexture,

            // PBR properties for lunar regolith (dusty rock surface)
            roughness: 0.95,      // Very rough - moon dust is highly diffuse
            metalness: 0.0,       // Non-metallic surface
            envMapIntensity: 0.1  // Minimal environment reflection
        };

        // Add displacement map if available (creates 3D crater depth)
        if (displacementTexture) {
            materialConfig.displacementMap = displacementTexture;
            materialConfig.displacementScale = 0.028;  // Pronounced craters
            console.log('✓ Displacement map applied');
        }

        // Add normal map if available (adds fine surface detail)
        if (normalTexture) {
            materialConfig.normalMap = normalTexture;
            materialConfig.normalScale = new THREE.Vector2(1.6, 1.6);  // Balanced detail
            console.log('✓ Normal map applied');
        }

        var material = new THREE.MeshStandardMaterial(materialConfig);
        
        moonMesh = new THREE.Mesh(geometry, material);
        // Never show an untextured (white) moon — visibility flips on as soon
        // as a map is applied (immediately when preloaded, else on first load).
        moonMesh.visible = !!materialConfig.map;
        moonGroup.add(moonMesh);
        
        // Initial rotation to show near-side of moon
        moonMesh.rotation.y = -THREE.MathUtils.degToRad(90);
        
        // Initial render
        updateMoon();
        render();
    }

    // ============================================
    // MOON PHASE, LIBRATION & LIGHTING UPDATE
    // ============================================
    function updateMoon() {
        if (!moonMesh) return;

        // Get moon illumination from SunCalc
        var moonIllum = SunCalc.getMoonIllumination(currentDate);
        var phaseName = getPhaseName(moonIllum.phase);
        var illuminationPercent = Math.round(moonIllum.fraction * 100);
        
        // Update bridge exports for moon-myjs.js
        updatePhaseInfo(moonIllum, phaseName);
        updateGlowStrength(moonIllum.fraction);
        window.moonCurrentLocation = currentLocation;
        
        console.log('🌙 Phase:', phaseName, 
                    '| Value:', moonIllum.phase.toFixed(3),
                    '| Illumination:', illuminationPercent + '%');

        // Directly apply transforms & lighting – no JSON step
        applyMoonTransforms(moonIllum, phaseName, illuminationPercent);
    }

    function applyMoonTransforms(moonIllum, phaseName, illuminationPercent) {
        // Compute position angle of axis (P) and bright limb (χ) directly
        var positionAngle = computeAxisPositionAngle(currentDate);     // radians
        var brightLimb    = computeBrightLimbAngle(currentDate);       // radians

        console.log('📊 Computed - Axis PA (P):', positionAngle.toFixed(4),
                    '| Bright Limb (χ):', brightLimb.toFixed(4));

        // ============================================
        // LIBRATION CALCULATION (Moon Wobble)
        // ============================================
        var libration = { elon: 0, elat: 0 };
        
        if (typeof Astronomy !== 'undefined' && Astronomy.Libration) {
            try {
                libration = Astronomy.Libration(currentDate);
                console.log('🔄 Libration - Lat:', libration.elat.toFixed(2) + '°',
                            'Lon:', libration.elon.toFixed(2) + '°');
            } catch(e) {
                console.warn('⚠️ Libration calculation failed:', e);
            }
        }

        // ============================================
        // CALCULATE PARALLACTIC ANGLE using Astronomy.js
        // ============================================
        var parallacticAngle = 0;
        
        if (typeof Astronomy !== 'undefined') {
            try {
                parallacticAngle = calculateParallacticAngleAstronomy(currentDate, currentLocation.lat, currentLocation.lng);
                console.log('🔄 Parallactic Angle:', THREE.MathUtils.radToDeg(parallacticAngle).toFixed(2) + '°');
            } catch(e) {
                console.warn('⚠️ Parallactic angle calculation failed:', e);
                parallacticAngle = calculateParallacticAngleSunCalc(currentDate, currentLocation.lat, currentLocation.lng);
            }
        } else {
            parallacticAngle = calculateParallacticAngleSunCalc(currentDate, currentLocation.lat, currentLocation.lng);
        }

        // ============================================
        // APPLY ROTATIONS
        // ============================================

        // Reset moon mesh rotations
        moonMesh.rotation.x = 0;
        moonMesh.rotation.y = -THREE.MathUtils.degToRad(90);  // Base orientation
        moonMesh.rotation.z = 0;

        // Reset moonGroup rotation
        moonGroup.rotation.set(0, 0, 0);

        // World axes for rotation
        var xAxis = new THREE.Vector3(1, 0, 0);
        var yAxis = new THREE.Vector3(0, 1, 0);
        var zAxis = new THREE.Vector3(0, 0, 1);

        // Convert libration to radians
        var lonLibration = THREE.MathUtils.degToRad(libration.elon);
        var latLibration = THREE.MathUtils.degToRad(libration.elat);

        // Apply to MOON MESH: libration + position angle (geocentric orientation)
        moonMesh.rotateOnWorldAxis(yAxis, -lonLibration);
        moonMesh.rotateOnWorldAxis(xAxis, latLibration);
        moonMesh.rotateOnWorldAxis(zAxis, positionAngle);

        // Apply to MOON GROUP: parallactic angle (observer's view rotation)
        // This rotates BOTH the moon AND the lights together
        moonGroup.rotation.z = -parallacticAngle;

        console.log('🔄 Rotation breakdown:');
        console.log('   Position Angle (P):', THREE.MathUtils.radToDeg(positionAngle).toFixed(2) + '°');
        console.log('   Parallactic Angle (q):', THREE.MathUtils.radToDeg(parallacticAngle).toFixed(2) + '°');
        console.log('   MoonGroup Z:', THREE.MathUtils.radToDeg(moonGroup.rotation.z).toFixed(2) + '°');

        // ============================================
        // UPDATE LIGHTING FOR PHASE
        // ============================================
        updateLightingForPhase(moonIllum, brightLimb);

        // Halo must follow the Sun — read the direction after the lights
        // have been positioned for this phase.
        updateGlowDirection();

        // Update UI displays
        updatePhaseDisplay(phaseName, illuminationPercent);

        // Decoupled listeners (Moon Data corner blocks in moon-skylit.liquid)
        // recompute from getMoonCurrentDate()/moonCurrentLocation on this.
        document.dispatchEvent(new CustomEvent('moon:updated'));

        render();
    }

    // ============================================
    // PARALLACTIC ANGLE using Astronomy.js (more accurate)
    // ============================================
    function calculateParallacticAngleAstronomy(date, lat, lng) {
        // Create observer
        var observer = new Astronomy.Observer(lat, lng, 0);
        
        // Get moon's equatorial coordinates
        var equ = Astronomy.Equator("Moon", date, observer, true, true);
        
        // Get moon's horizontal coordinates
        var hor = Astronomy.Horizon(date, observer, equ.ra, equ.dec, 'normal');
        
        // Calculate hour angle
        var gast = Astronomy.SiderealTime(date);
        var hourAngle = (gast + lng / 15 - equ.ra) * 15;  // Convert to degrees
        hourAngle = THREE.MathUtils.degToRad(hourAngle);
        
        var latRad = THREE.MathUtils.degToRad(lat);
        var decRad = THREE.MathUtils.degToRad(equ.dec);
        
        // Parallactic angle formula using hour angle
        var sinQ = Math.sin(hourAngle) * Math.cos(latRad);
        var cosQ = Math.sin(latRad) * Math.cos(decRad) - Math.cos(latRad) * Math.sin(decRad) * Math.cos(hourAngle);
        
        var parallacticAngle = Math.atan2(sinQ, cosQ);
        
        console.log('🌙 Moon RA:', equ.ra.toFixed(4), 'Dec:', equ.dec.toFixed(4),
                    'HA:', THREE.MathUtils.radToDeg(hourAngle).toFixed(2) + '°');
        
        return parallacticAngle;
    }

    function deg2rad(d) {
        return d * Math.PI / 180;
    }

    // Meeus 46.5: position angle of the Moon's bright limb χ
    // Uses geocentric RA/Dec of Sun and Moon. 
    function computeBrightLimbAngle(date) {
        // Geocentric observer at Earth's centre (parallax doesn't matter for prints)
        var observer = new Astronomy.Observer(0, 0, 0);

        // Equatorial coordinates of Moon and Sun, equator-of-date
        var moonEqu = Astronomy.Equator('Moon', date, observer, true, true);
        var sunEqu  = Astronomy.Equator('Sun',  date, observer, true, true);

        // RA from hours → radians, Dec from degrees → radians
        var alpha   = deg2rad(moonEqu.ra * 15);
        var delta   = deg2rad(moonEqu.dec);
        var alpha0  = deg2rad(sunEqu.ra * 15);
        var delta0  = deg2rad(sunEqu.dec);

        var num = Math.cos(delta0) * Math.sin(alpha0 - alpha);
        var den = Math.sin(delta0) * Math.cos(delta) -
                  Math.cos(delta0) * Math.sin(delta) * Math.cos(alpha0 - alpha);

        // χ: position angle of bright limb, measured from celestial north toward east
        return Math.atan2(num, den);
    }

    // Approx IAU pole model: position angle P of the Moon's rotation axis. 
    function computeAxisPositionAngle(date) {
        // J2000 IAU mean pole; we ignore the tiny time-dependent nutation terms
        var RA_P  = deg2rad(269.9949);
        var DEC_P = deg2rad(66.5392);

        var observer = new Astronomy.Observer(0, 0, 0);
        var moonEqu  = Astronomy.Equator('Moon', date, observer, true, true);

        var alpha = deg2rad(moonEqu.ra * 15);
        var delta = deg2rad(moonEqu.dec);

        var num = Math.cos(DEC_P) * Math.sin(RA_P - alpha);
        var den = Math.sin(DEC_P) * Math.cos(delta) -
                  Math.cos(DEC_P) * Math.sin(delta) * Math.cos(RA_P - alpha);

        // P: position angle of lunar north pole, measured from celestial north toward east
        return Math.atan2(num, den);
    }

    // ============================================
    // PARALLACTIC ANGLE using SunCalc (fallback)
    // ============================================
    function calculateParallacticAngleSunCalc(date, lat, lng) {
        var moonPos = SunCalc.getMoonPosition(date, lat, lng);
        
        var altitude = moonPos.altitude;
        var azimuth = moonPos.azimuth;
        var latitude = THREE.MathUtils.degToRad(lat);
        
        // Handle moon below horizon
        if (altitude < 0) {
            console.log('🌙 Moon below horizon, using approximation');
            // When moon is below horizon, use a simplified calculation
            return 0;
        }
        
        var sinAz = Math.sin(azimuth);
        var cosAz = Math.cos(azimuth);
        var sinLat = Math.sin(latitude);
        var cosLat = Math.cos(latitude);
        var tanAlt = Math.tan(altitude);
        
        var parallacticAngle = Math.atan2(sinAz, cosAz * sinLat - tanAlt * cosLat);
        
        console.log('🌙 Moon Position - Alt:', THREE.MathUtils.radToDeg(altitude).toFixed(2) + '°',
                    'Az:', THREE.MathUtils.radToDeg(azimuth).toFixed(2) + '°');
        
        return parallacticAngle;
    }

    // ============================================
    // LIGHTING POSITION CALCULATION
    // ============================================
    function updateLightingForPhase(moonIllum, brightLimb) {
        var fraction = moonIllum.fraction;

        // Calculate light direction based on phase and bright limb angle
        // Using spherical coordinates to position the directional light
        var F = Math.PI / 2 - brightLimb;
        var C = Math.acos(1 - 2 * fraction);

        // Light position (normalized direction for DirectionalLight)
        var lightX = -Math.sin(C) * Math.cos(F);
        var lightY =  Math.sin(C) * Math.sin(F);
        var lightZ = -Math.cos(C);

        // Update directional light position
        lights.directional.position.set(lightX * 100, lightY * 100, lightZ * 100);

        // Earthshine = opposite direction, much weaker
        lights.earthshine.position
            .set(-lightX * 100, -lightY * 100, -lightZ * 100);

        // PBR intensity adjustments based on phase
        // Higher values needed for physically-based materials
        if (fraction < 0.25) {
            // Thin crescent - boost ambient for dark side visibility
            lights.ambient.intensity = 0.35;
            lights.directional.intensity = 2.6;
        } else if (fraction < 0.5) {
            // Quarter to half - balanced
            lights.ambient.intensity = 0.3;
            lights.directional.intensity = 2.8;
        } else {
            // Gibbous to full - standard PBR levels
            lights.ambient.intensity = 0.25;
            lights.directional.intensity = 2.4;
        }

        console.log('💡 Bright Limb (χ):', brightLimb.toFixed(4),
                    '| Illumination:', (fraction * 100).toFixed(1) + '%');
        console.log('💡 Light direction: X=' + lightX.toFixed(3) +
                    ', Y=' + lightY.toFixed(3) +
                    ', Z=' + lightZ.toFixed(3));
    }

    // ============================================
    // PHASE NAME HELPER
    // ============================================
    function getPhaseName(phase) {
        if (phase < 0.025 || phase > 0.975) return 'New Moon';
        if (phase < 0.225) return 'Waxing Crescent';
        if (phase < 0.275) return 'First Quarter';
        if (phase < 0.475) return 'Waxing Gibbous';
        if (phase < 0.525) return 'Full Moon';
        if (phase < 0.725) return 'Waning Gibbous';
        if (phase < 0.775) return 'Last Quarter';
        return 'Waning Crescent';
    }

    // ============================================
    // RENDER
    // ============================================
    function render() {
        if (renderer && scene && camera) {
            renderer.render(scene, camera);
            // First successful draw — signal the headless boot path that the
            // moon disc is now on the canvas (covers cold boot, texture
            // variant change, date/location change — all route through here).
            signalMoonRendered();
            updateGlowSize();
            scheduleGlowTint();
        }
    }

    // ============================================
    // DATE/TIME SELECTORS
    // ============================================
    function initDateTimeSelectors() {
        var now = new Date();
        
        // Populate Days (1-31)
        var daysHtml = '';
        for (var d = 1; d <= 31; d++) {
            var selected = (d === now.getDate()) ? 'selected' : '';
            daysHtml += '<option value="' + d + '" ' + selected + '>' + d + '</option>';
        }
        setHTML('.bear-dates', daysHtml);
        
        // Populate Months
        var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
        var monthsHtml = '';
        for (var m = 0; m < 12; m++) {
            var selected = (m === now.getMonth()) ? 'selected' : '';
            monthsHtml += '<option value="' + m + '" ' + selected + '>' + monthNames[m] + '</option>';
        }
        setHTML('.bear-months', monthsHtml);
        
        // Populate Years (1920 - 2035)
        var currentYear = now.getFullYear();
        var yearsHtml = '';
        for (var y = 1920; y <= 2035; y++) {
            var selected = (y === currentYear) ? 'selected' : '';
            yearsHtml += '<option value="' + y + '" ' + selected + '>' + y + '</option>';
        }
        setHTML('.bear-years', yearsHtml);
        
        // Populate Hours (1-12 for 12-hour format) — default 11 (for 11:11 PM)
        var hoursHtml = '';
        for (var h = 1; h <= 12; h++) {
            var selected = (h === 11) ? 'selected' : '';
            hoursHtml += '<option value="' + h + '" ' + selected + '>' + h + '</option>';
        }
        setHTML('.moon-hours', hoursHtml);

        // Populate Minutes (0-59) — default 11 (for 11:11 PM)
        var minutesHtml = '';
        for (var min = 0; min <= 59; min++) {
            var displayMin = min.toString().padStart(2, '0');
            var selected = (min === 11) ? 'selected' : '';
            minutesHtml += '<option value="' + min + '" ' + selected + '>' + displayMin + '</option>';
        }
        setHTML('.moon-minutes', minutesHtml);

        // Populate Meridiem (AM/PM)
        var meridiemHtml = '<option value="AM">AM</option><option value="PM" selected>PM</option>';
        setHTML('.moon-meridiem', meridiemHtml);

        // Change Handlers
        all('.bear-dates, .bear-months, .bear-years, .moon-hours, .moon-minutes, .moon-meridiem').forEach(function (sel) {
            sel.addEventListener('change', function () {
                updateDateFromSelectors();
                reloadMoonWithAnimation();
            });
        });
        
        // Set initial values
        updateDateFromSelectors();
        updateDateDisplay();
        updateCoordinatesDisplay();
        
        console.log('📅 Date/time selectors initialized');
    }

    function updateDateFromSelectors() {
        var day = parseInt(getVal('.bear-dates')) || 1;
        var month = parseInt(getVal('.bear-months')) || 0;
        var year = parseInt(getVal('.bear-years')) || new Date().getFullYear();

        // Get 12-hour format values
        var hours12 = parseInt(getVal('.moon-hours')) || 9;
        var minutesVal = parseInt(getVal('.moon-minutes'));
        var minutes = isNaN(minutesVal) ? 0 : minutesVal;
        var meridiem = getVal('.moon-meridiem') || 'PM';

        // Convert 12-hour to 24-hour
        var hours24;
        if (meridiem === 'AM') {
            hours24 = (hours12 === 12) ? 0 : hours12;
        } else {
            hours24 = (hours12 === 12) ? 12 : hours12 + 12;
        }

        currentDate = new Date(year, month, day, hours24, minutes);
        console.log('📅 Date updated:', currentDate.toLocaleString(), '(12h:', hours12, meridiem, ')');

        updateDateDisplay();
    }

    // ============================================
    // LOADING ANIMATION
    // ============================================
    // The star loader over the preview (fade helper lives in moon-myjs.js).
    function showLoader() {
        if (window.moonFadeLoader) window.moonFadeLoader(true, 200);
    }

    function hideLoader() {
        if (window.moonFadeLoader) window.moonFadeLoader(false, 300);
    }
    
    function reloadMoonWithAnimation() {
        showLoader();
        
        // Small delay for visual feedback
        setTimeout(function() {
            updateMoon();
            hideLoader();
        }, 100);
    }

    // ============================================
    // DISPLAY UPDATES
    // ============================================
    function updatePhaseDisplay(phaseName, illuminationPercent) {
        setText('#phase-display', phaseName + ' (' + illuminationPercent + '% illuminated)');
    }
    
    function updateDateDisplay() {
        var day = currentDate.getDate();
        var monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        var month = monthNames[currentDate.getMonth()];
        var year = currentDate.getFullYear();
        var hours24 = currentDate.getHours();
        var minutes = currentDate.getMinutes();

        // Convert 24-hour to 12-hour format
        var hours12 = (hours24 === 0) ? 12 : (hours24 > 12 ? hours24 - 12 : hours24);
        var meridiem = hours24 >= 12 ? 'PM' : 'AM';

        // Get ordinal suffix for day
        function getOrdinalSuffix(d) {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        }

        // No comma after the month, matching the star map print (2026-11-01).
        var dateStr = day + getOrdinalSuffix(day) + ' ' + month + ' ' + year;
        // 12-hour format without leading zero on hour
        var timeStr = hours12 + ':' + minutes.toString().padStart(2, '0') + ' ' + meridiem;

        // Check if time should be displayed (toggle is checked = show time)
        var showTime = document.getElementById('moon_time_on');
        var computed = (showTime && showTime.checked) ? (dateStr + ' - ' + timeStr) : dateStr;

        // Smart override sync: if the user hasn't manually edited
        // #custom_date_text, keep it (and the preview) in lockstep with the
        // computed default so toggling time on/off or changing the date
        // re-derives the field. If they HAVE edited it, leave their text
        // alone and just mirror it into the preview line.
        if (window.moonDateOverrideDirty) {
            var userDate = (getVal('#custom_date_text') || '').trim();
            setText('#custom_date', userDate);
        } else {
            // updateDateDisplay only fires on init + date/time/show-time changes
            // (never on a plain re-render), so a date change re-derives the
            // printed date and cancels any prior "deliberately cleared" state.
            window.moonDateOverrideCleared = false;
            setVal('#custom_date_text', computed);
            setText('#custom_date', computed);
        }
    }

    function updateCoordinatesDisplay() {
        var latDir = currentLocation.lat >= 0 ? 'N' : 'S';
        var lngDir = currentLocation.lng >= 0 ? 'E' : 'W';
        var coordStr = Math.abs(currentLocation.lat).toFixed(2) + '° ' + latDir + ', ' +
                       Math.abs(currentLocation.lng).toFixed(2) + '° ' + lngDir;

        // Same dirty-flag guard as the date field: keep #custom_lat_long synced
        // to the computed coordinates unless the user has typed their own.
        if (window.moonCoordsOverrideDirty) {
            var userCoords = (getVal('#custom_lat_long') || '').trim();
            setText('#custom_lat_lng', userCoords);
        } else if (window.moonCoordsOverrideCleared) {
            // User deliberately emptied the printed-coordinates field: keep the
            // input blank and print a blank line (non-breaking space) so the
            // spacing is preserved instead of falling back to the auto value.
            setText('#custom_lat_lng', String.fromCharCode(160));
        } else {
            setVal('#custom_lat_long', coordStr);
            setText('#custom_lat_lng', coordStr);
        }
    }

    // ============================================
    // UI EVENT HANDLERS
    // ============================================
    // The sidebar itself is moon-ui.js; the engine only listens for the two
    // inputs that change what it draws.
    function setupUI() {
        // A location typed or picked into the hidden #address_form1 mirror
        // (change fires even without an autocomplete selection).
        var addr = first('#address_form1');
        if (addr) addr.addEventListener('change', function () {
            setTimeout(function () { reloadMoonWithAnimation(); }, 100);
        });
        // Show-time toggle re-derives the printed dateline.
        var timeOn = first('#moon_time_on');
        if (timeOn) timeOn.addEventListener('change', function () { updateDateDisplay(); });
        console.log('🎨 UI initialized');
    }

    // A solid background colour also fills the WebGL scene behind the disc.
    // 'transparent' or rgba alpha=0 → null, so the CSS background shows
    // through.
    window.moonSetSceneBackground = function (bgColor) {
        if (!scene) return;
        var bgLower = String(bgColor || '').toLowerCase().trim();
        var transparent = !bgLower || bgLower === 'transparent' ||
            /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0(?:\.0+)?\s*\)$/.test(bgLower);
        scene.background = transparent ? null : new THREE.Color(bgColor);
        render();
    };

    // ============================================
    // UTILITIES
    // ============================================
    function debounce(func, wait) {
        var timeout;
        return function() {
            var context = this, args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(function() {
                func.apply(context, args);
            }, wait);
        };
    }

    function handleResize() {
        updateGlowSize();
        var container = document.getElementById('moon-canvas-container');
        if (container && renderer) {
            var containerWidth = container.offsetWidth || 400;
            // Mirror the init sizing — earlier this was `* 0.85` which
            // silently shrunk the moon by 15% on every resize call (URL
            // bar toggle, pinch zoom, etc.), making the same canvas look
            // smaller after any spurious resize event.
            var size = Math.min(containerWidth, 600);
            renderer.setSize(size, size);
            camera.updateProjectionMatrix();
            render();
        }
    }

    // ============================================
    // EXPORT FUNCTIONS (for high-res print & cart)
    // ============================================

    // Async version - loads high-res textures before export (recommended)
    window.exportMoonCanvasAsync = async function(width, height) {
        if (!renderer) return null;

        console.log('🌙 Loading high-res textures for export...');

        // Load high-res textures and wait for completion
        await loadHighResTextures();

        var originalSize = renderer.getSize(new THREE.Vector2());

        // Temporarily resize for export
        renderer.setSize(width || 2048, height || 2048);
        render();

        var dataURL = renderer.domElement.toDataURL('image/jpeg', 0.95);

        // Restore original size
        renderer.setSize(originalSize.x, originalSize.y);

        // Restore preview textures for continued browsing
        loadPreviewTextures();

        console.log('🌙 High-res export complete');
        return dataURL;
    };

    // Sync version - uses current textures (fallback for compatibility)
    window.exportMoonCanvas = function(width, height) {
        if (!renderer) return null;

        var originalSize = renderer.getSize(new THREE.Vector2());

        // Temporarily resize for export
        renderer.setSize(width || 2048, height || 2048);
        render();

        var dataURL = renderer.domElement.toDataURL('image/jpeg', 0.95);

        // Restore original size
        renderer.setSize(originalSize.x, originalSize.y);
        render();

        return dataURL;
    };

    // Expose update function for external triggers
    window.updateMoonMap = function() {
        reloadMoonWithAnimation();
    };
    
    // Expose function to set location from external code (moon-myjs.js)
    window.setMoonLocationInternal = function(lat, lng, name) {
        currentLocation.lat = lat;
        currentLocation.lng = lng;
        currentLocation.name = name;
        window.moonCurrentLocation = currentLocation;
        
        // Update display and reload moon
        updateCoordinatesDisplay();
        reloadMoonWithAnimation();
    };
    
    // Expose current date for external access
    window.getMoonCurrentDate = function() {
        return currentDate;
    };
    
    // Expose function to set date from external code
    window.setMoonDate = function(date) {
        currentDate = date;
        updateMoon();
    };

})();
